'use client';

import type { CartItem } from '@/lib/atoms/cart-atoms';
import type { Variant } from '@/types/custom';
import { getCartUrl } from '@/utils/cart-url';
import { cn } from '@/utils/cn';
import { validateStock } from '@/utils/stock-validation';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

interface VariantSelectorProps {
    variants: Variant[];
    selectedVariantId: number | null;
    onVariantSelect: (id: number) => void;
    className?: string;
    cartItems?: CartItem[];
}

/**
 * A reusable component for selecting product variants
 */
export default function VariantSelector({
    variants,
    selectedVariantId,
    onVariantSelect,
    className = '',
    cartItems = [],
}: VariantSelectorProps) {
    const t = useTranslations('Cart');
    const locale = useLocale();

    // Get cart URL using helper
    const cartUrl = getCartUrl(locale);

    if (variants.length === 0) {
        return null;
    }

    // Helper function to get cart quantity for a variant
    const getCartQuantity = (variantId: number): number => {
        const cartItem = cartItems.find((item) => item.purchasable.id === variantId);
        return cartItem ? cartItem.qty : 0;
    };

    return (
        <div className={`mb-8 ${className}`}>
            <div className="space-y-3">
                {variants.map((variant) => {
                    const cartQuantity = getCartQuantity(variant.id);

                    // Use centralized stock validation
                    const stockStatus = validateStock(
                        {
                            variant,
                            currentCartQuantity: cartQuantity,
                            requestedQuantity: 1,
                        },
                        {
                            outOfStock: t('outOfStock'),
                            inCart: (params) => t('inCart', params),
                            lastItemInCart: t('lastItemInCart'),
                            allItemsInCart: (params) => t('allItemsInCart', params),
                            insufficientStock: (params) => t('insufficientStock', params),
                            stockLimitExceededWithDetails: (params) =>
                                t('stockLimitExceededWithDetails', params),
                        }
                    );

                    const isDisabled = !stockStatus.canIncrease && cartQuantity === 0;

                    return (
                        <button
                            key={variant.id}
                            type="button"
                            onClick={() => onVariantSelect(variant.id)}
                            className={cn(
                                'w-full rounded border p-4 text-left transition-colors',
                                'font-neue-haas',
                                'border-haug-lila-200',
                                selectedVariantId === null ? 'bg-haug-lila-100 text-black' : 'bg-haug-ivory-100 text-black',
                                selectedVariantId === variant.id
                                    ? 'bg-haug-lila-30 text-black border-haug-lila-200'
                                    : 'border-haug-choco-30',
                                isDisabled ? 'opacity-70' : 'hover:border-haug-choco-70'
                            )}
                            disabled={isDisabled}
                        >
                            <div className="flex items-center justify-between">
                                <div
                                    className={cn(
                                        'flex-1',
                                        isDisabled ? 'cursor-not-allowed opacity-30' : 'opacity-100'
                                    )}
                                >
                                    <div className="font-bold text-lg">{variant.name}</div>
                                    <div className="font-medium text-xl">
                                        €{(variant.price || 0).toFixed(2)}
                                    </div>
                                </div>
                                <div className="text-right">
                                    {stockStatus.statusMessage &&
                                        (stockStatus.isOutOfStock ? (
                                            <div
                                                className={cn(
                                                    'font-bold',
                                                    selectedVariantId === variant.id
                                                        ? 'text-white'
                                                        : 'text-red-500'
                                                )}
                                            >
                                                {stockStatus.statusMessage}
                                            </div>
                                        ) : (
                                            <Link
                                                href={cartUrl}
                                                className={cn(
                                                    'font-bold underline transition-colors',
                                                    selectedVariantId === variant.id
                                                        ? 'text-black hover:text-gray-200'
                                                        : 'text-haug-choco-100 hover:text-haug-lila-100'
                                                )}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {stockStatus.statusMessage}
                                            </Link>
                                        ))}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
